import { Box, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';

interface OutdoorBackgroundProps {
  children: ReactNode;
  variant?: 'light' | 'dark';
  density?: 'low' | 'medium' | 'high';
  sx?: SxProps<Theme>;
}

/**
 * A component that adds a subtle outdoorsy background effect to any container
 */
const OutdoorBackground = ({ 
  children, 
  variant = 'light',
  density = 'medium',
  sx
}: OutdoorBackgroundProps) => {
  // Configure the appearance based on variant and density
  const getBackgroundConfig = () => {
    const baseColor = variant === 'light' 
      ? 'rgba(245, 241, 232, 0.8)' 
      : 'rgba(59, 82, 73, 0.8)';
    
    const patternOpacity = {
      low: 0.03,
      medium: 0.05,
      high: 0.08
    }[density];
    
    const overlayColor = variant === 'light'
      ? `rgba(217, 165, 102, ${patternOpacity})`
      : `rgba(42, 60, 52, ${patternOpacity})`;
    
    return {
      baseColor,
      overlayColor,
      patternSize: density === 'low' ? '80px' : density === 'medium' ? '60px' : '40px'
    };
  };
  
  const { baseColor, overlayColor, patternSize } = getBackgroundConfig();
  
  return (
    <Box sx={{ 
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      backgroundColor: baseColor,
      backgroundImage: `
        linear-gradient(${overlayColor} 1px, transparent 1px),
        linear-gradient(to right, ${overlayColor} 1px, transparent 1px)
      `,
      backgroundSize: `${patternSize} ${patternSize}`,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M50 25L65 40L50 55L35 40L50 25Z\' fill=\'rgba(94, 115, 70, 0.03)\' /%3E%3C/svg%3E")',
        backgroundSize: '100px 100px',
        opacity: 0.5,
        pointerEvents: 'none',
        zIndex: 0
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: variant === 'light' 
          ? 'linear-gradient(45deg, rgba(59, 82, 73, 0.01) 25%, transparent 25%, transparent 50%, rgba(59, 82, 73, 0.01) 50%, rgba(59, 82, 73, 0.01) 75%, transparent 75%, transparent)'
          : 'linear-gradient(45deg, rgba(217, 165, 102, 0.01) 25%, transparent 25%, transparent 50%, rgba(217, 165, 102, 0.01) 50%, rgba(217, 165, 102, 0.01) 75%, transparent 75%, transparent)',
        backgroundSize: '20px 20px',
        opacity: 0.6,
        pointerEvents: 'none',
        zIndex: 0
      },
      ...sx
    }}>
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {children}
      </Box>
    </Box>
  );
};

export default OutdoorBackground; 